import React, { useMemo } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { type Edges, SafeAreaView } from 'react-native-safe-area-context';

import { Icon, RotatingIcon, theme, useResponsive } from '@sg/garnish';

import {
  OrderPlaybackFadeInView,
  OrderPlaybackFooter,
  OrderPlaybackLineItems,
  OrderPlaybackLocation,
  OrderPlaybackOrderDetails,
  OrderPlaybackTime,
  OrderPlaybackTimer,
  OrderPlaybackTitle,
} from './components';
import { getTime, useDate } from './utils';
import { type BagOrderPlaybackProps } from './utils';

/**
 * Order playback component for displaying order details.
 */
export const BagOrderPlayback = (props: BagOrderPlaybackProps) => {
  const {
    products,
    wantedTime = new Date(),
    isWantedTimeAsap,
    orderChannel,
    locationName,
    orderPlaybackTitle,
    orderPlaybackFooterTitle,
    orderPlaybackFooterLoading,
    orderPlaybackFooterCta,
    orderPlaybackConfig,
    isLoading,
    isPostOrder,
    goBack,
  } = props;

  const { playbackHeaderFadeDuration, playbackBodyFadeDuration, playbackTimerDuration } =
    orderPlaybackConfig;

  const date = useDate(wantedTime, isWantedTimeAsap);
  const time = getTime(wantedTime, orderChannel);
  const count = useMemo(() => {
    return products.reduce((acc, product) => acc + product.quantity, 0);
  }, [products]);

  const { minWidth, currentHeight } = useResponsive();
  const withSmallHeight = minWidth.isSM && currentHeight.isSM;

  const headerFadeIn = playbackHeaderFadeDuration;
  const bodyFadeIn = playbackHeaderFadeDuration + playbackBodyFadeDuration;
  const timerDuration = playbackTimerDuration;

  const footerTitle =
    isLoading || isPostOrder ? orderPlaybackFooterLoading : orderPlaybackFooterTitle;

  return (
    <SafeAreaView
      edges={SAFE_AREA_EDGES}
      style={[styles.container, withSmallHeight && styles.containerSmallHeight]}
    >
      <View style={styles.headerContent}>
        <View style={styles.titleContainer}>
          <OrderPlaybackTitle title={orderPlaybackTitle} />

          {isPostOrder ? (
            <Icon name="IconBadgeCheck" color={theme.colors.KALE} width={24} height={24} />
          ) : (
            <RotatingIcon
              duration={HEADER_ICON_ROTATION_DURATION}
              delay={HEADER_ICON_ROTATION_DELAY}
              color={theme.colors.KALE}
              name="IconOrderPlaybackHexagon"
              width={24}
              height={24}
            />
          )}
        </View>

        <OrderPlaybackFadeInView fadeIn={isLoading ? 0 : headerFadeIn}>
          <OrderPlaybackLocation orderChannel={orderChannel} locationName={locationName} />
        </OrderPlaybackFadeInView>

        <OrderPlaybackFadeInView fadeIn={isLoading ? 0 : headerFadeIn}>
          <OrderPlaybackTime orderChannel={orderChannel} date={date} time={time} />
        </OrderPlaybackFadeInView>

        <OrderPlaybackFadeInView fadeIn={isLoading ? 0 : headerFadeIn}>
          <OrderPlaybackOrderDetails count={count} />
        </OrderPlaybackFadeInView>
      </View>

      <OrderPlaybackFadeInView fadeIn={isLoading ? 0 : headerFadeIn}>
        <OrderPlaybackTimer delay={bodyFadeIn} duration={timerDuration} skipAnimation={isLoading} />
      </OrderPlaybackFadeInView>

      <View style={styles.bodyContent}>
        <OrderPlaybackFadeInView style={styles.lineItems} fadeIn={isLoading ? 0 : bodyFadeIn}>
          <OrderPlaybackLineItems fadeIn={isLoading ? 0 : bodyFadeIn} products={products} />
        </OrderPlaybackFadeInView>
      </View>

      <OrderPlaybackFooter
        isLoading={isLoading || isPostOrder}
        title={footerTitle}
        cta={orderPlaybackFooterCta}
        onPressCta={goBack}
      />
    </SafeAreaView>
  );
};

// ─── Constants ────────────────────────────────────────────────────────────────

const SAFE_AREA_EDGES: Edges = Platform.select({
  android: ['top'],
  default: [],
});

const HEADER_ICON_ROTATION_DURATION = 600;
const HEADER_ICON_ROTATION_DELAY = 400;

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    paddingTop: theme.spacing['10'],
  },
  headerContent: {
    gap: theme.spacing['4'],
    paddingHorizontal: theme.spacing['4'],
    marginBottom: theme.spacing['4'],
  },
  bodyContent: {
    flexGrow: 1,
    gap: theme.spacing['4'],
    paddingHorizontal: theme.spacing['4'],
    marginTop: theme.spacing['4'],
  },
  containerSmallHeight: {
    paddingTop: theme.spacing['4'],
    paddingBottom: theme.spacing['4'],
  },
  titleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  lineItems: {
    flex: 1,
    gap: theme.spacing['4'],
  },
});
